import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import * as Yup from 'yup';
import { ticketsType } from '../../../services/TicketsServices';
import SimpleConfigTable from './shared/SimpleConfigTable';
import ConfirmDeleteDialog from './shared/ConfirmDeleteDialog';
import TicketTypeFormDialog from './shared/TicketTypeFormDialog';
import TablePagerBar from './shared/TablePagerBar';
import useTicketTypes from './shared/useTicketTypes';
import { messages } from '../../../common/Models/Validations';
import { useNavigate } from 'react-router-dom';

const ticketTypeValidation = Yup.object().shape({
  code: Yup.string().required(messages.required),
  name: Yup.string().required(messages.required),
  description: Yup.string().required(messages.required),
  museum_type_id: Yup.number().required(messages.required),
  visitor_type_id: Yup.number().required(messages.required),
  fee: Yup.number().required(messages.required),
  status: Yup.string().required(messages.required)
});

export default function TicketTypesPanel() {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const navigate = useNavigate();
  const {
    items,
    activeFilter,
    pagination,
    deleteTarget,
    deleteLoading,
    fetchList,
    changePage,
    changePerPage,
    changeActiveFilter,
    requestDelete,
    cancelDelete,
    confirmDelete,
  } = useTicketTypes();

  const handleSubmit = async (values, { setSubmitting }) => {
    const payload = {
      code: values.code,
      name: values.name,
      description: values.description,
      museum_type_id: values.museum_type_id,
      visitor_type_id: values.visitor_type_id,
      fee: values.fee,
      status: values.status,
    };

    if (editingItem) {
      try {
        const res = await ticketsType('put', payload, null, editingItem.id);
        fetchList(pagination.page, pagination.perPage, activeFilter);
        setOpenDialog(false);
        setEditingItem(null);
      } catch (err) {
        console.error('Failed to update ticket type:', err);
      } finally {
        setSubmitting(false);
      }
    } else {
      try {
        const res = await ticketsType('post', payload);
        fetchList(1, pagination.perPage, activeFilter);
        setOpenDialog(false);
        setEditingItem(null);
      } catch (err) {
        console.error('Failed to create ticket type:', err);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleEdit = (item) => {
    console.log('handleEdit: Edit clicked for item', item);
    setEditingItem(item?.original || item);
    setOpenDialog(true);
  };

  const handleAdd = () => {
    console.log('handleAdd: Add Ticket Type clicked');
    setEditingItem(null);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setEditingItem(null);
  };

  const handleRowClick = (row) => {
    if (!row?.id) return;
    navigate(`/management/ticket-management/${row.id}`);
  };

  const columns = [
    { key: 'name', label: 'Museum' },
    { key: 'ticket_type_name', label: 'Ticket Name' },
    { key: 'visitor_type_name', label: 'Visitor Type' },
    { key: 'fee', label: 'Fee' }
  ];

  const rows = items.map(item => ({
    id: item.id,
    name: item?.museum_type?.name || item?.museum_type_name || '',
    ticket_type_name: item?.name || '',
    visitor_type_name: item?.visitor_type?.name || item?.visitor_type_name || '',
    fee: item?.fee ?? '',
    original: item
  }));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Ticket Fee Management</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Active</InputLabel>
            <Select
              value={activeFilter}
              label="Active"
              onChange={(e) => changeActiveFilter(e.target.value)}
            >
              <MenuItem value={'all'}>All</MenuItem>
              <MenuItem value={'active'}>Active</MenuItem>
              <MenuItem value={'inactive'}>Inactive</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleAdd}>
            Add Ticket Fee
          </Button>
        </Box>
      </Box>

      <Box>
        <SimpleConfigTable
          columns={columns}
          rows={rows}
          onEdit={handleEdit}
          onLogicalDelete={requestDelete}
          onRowClick={handleRowClick}
        />
        <TablePagerBar
          perPage={pagination.perPage}
          onPerPageChange={(e) => changePerPage(Number(e.target.value))}
          total={pagination.total}
          page={pagination.page}
          onPageChange={(_, page) => changePage(page)}
        />
      </Box>

      <TicketTypeFormDialog
        open={openDialog}
        onClose={handleClose}
        editingItem={editingItem}
        validationSchema={ticketTypeValidation}
        onSubmit={handleSubmit}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        title={'Delete ticket Fee'}
        description={'Are you sure you want to delete this ticket Fee?'}
        onCancel={cancelDelete}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </Box>
  );
}
